import React, { useState, useEffect, useRef } from 'react';
import {
  collection, addDoc, onSnapshot, query,
  orderBy, doc, getDoc, updateDoc, getDocs, where
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate, useParams } from 'react-router-dom';

function Chat() {
  const { jobRequestId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [jobRequest, setJobRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasSystemMessage, setHasSystemMessage] = useState(false);
  const [user, setUser] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !jobRequestId) return;
    const init = async () => {
      try {
        const jobSnap = await getDoc(doc(db, 'jobRequests', jobRequestId));
        const jobData = jobSnap.exists() ? jobSnap.data() : null;
        if (jobData) setJobRequest({ id: jobSnap.id, ...jobData });

        // Query conversations the SAME way Messages.js does (rules-compliant):
        // by participant, then match jobRequestId in JS.
        const q1 = query(collection(db, 'conversations'), where('workerId', '==', user.uid));
        const q2 = query(collection(db, 'conversations'), where('contractorId', '==', user.uid));
        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const allDocs = [...snap1.docs, ...snap2.docs];
        const match = allDocs.find(d => d.data().jobRequestId === jobRequestId);

        let convId;
        if (match) {
          convId = match.id;
        } else if (jobData) {
          // No conversation yet — create one (only possible if user is a participant)
          const newConv = await addDoc(collection(db, 'conversations'), {
            jobRequestId,
            workerId: jobData.workerId,
            contractorId: jobData.contractorId,
            workerName: jobData.workerName,
            createdAt: new Date(),
            lastMessage: '',
            lastMessageAt: new Date(),
          });
          convId = newConv.id;
        }

        if (convId) {
          setConversationId(convId);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Chat init error:', err);
        setLoading(false);
      }
    };
    init();
  }, [jobRequestId, user]);

  useEffect(() => {
    if (!conversationId) return;
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setHasSystemMessage(msgs.some(m => m.system));
      setLoading(false);
    }, (err) => {
      console.error('Messages snapshot error:', err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !conversationId || sending) return;
    setSending(true);
    const text = newMessage.trim();
    setNewMessage('');
    try {
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        text,
        senderId: user.uid,
        senderName: jobRequest?.workerId === user.uid
          ? jobRequest?.workerName
          : jobRequest?.contractorEmail || 'Contractor',
        system: false,
        createdAt: new Date(),
      });
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: text,
        lastMessageAt: new Date(),
      });
    } catch (err) {
      console.error('Send error:', err);
      setNewMessage(text);
      alert('Could not send message. Please try again.');
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      if (isToday) return timeStr;
      if (isYesterday) return `Yesterday ${timeStr}`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ` ${timeStr}`;
    } catch { return ''; }
  };

  const isMyMessage = (msg) => msg.senderId === user?.uid;

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return { bg: '#d4edda', color: '#155724' };
      case 'pending': return { bg: '#fff3cd', color: '#856404' };
      case 'declined': return { bg: '#f8d7da', color: '#721c24' };
      case 'completed': return { bg: '#e8f4fd', color: '#2c5282' };
      default: return { bg: '#f0f0f0', color: '#666' };
    }
  };

  const statusColors = getStatusColor(jobRequest?.status);

  if (loading) return (
    <div style={styles.loadingContainer}>
      <p style={styles.loadingText}>Loading conversation...</p>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/messages')}>← Messages</button>
      </div>

      {jobRequest && (
        <div style={styles.jobBar}>
          <div style={styles.jobBarLeft}>
            <div style={styles.jobBarTop}>
              <span style={styles.jobBarName}>
                {jobRequest.workerId === user?.uid
                  ? `🏗️ ${jobRequest.contractorEmail}`
                  : `👷 ${jobRequest.workerName}`}
              </span>
              <span style={{ ...styles.jobStatusBadge, backgroundColor: statusColors.bg, color: statusColors.color }}>
                {jobRequest.status?.charAt(0).toUpperCase() + jobRequest.status?.slice(1)}
              </span>
            </div>
            <p style={styles.jobBarDesc}>{jobRequest.jobDescription}</p>
            <p style={styles.jobBarDetails}>📍 {jobRequest.jobAddress} &nbsp;•&nbsp; 💰 ${jobRequest.contractorRate}/hr</p>
          </div>
          <button style={styles.viewJobBtn}
            onClick={() => navigate(jobRequest.workerId === user?.uid ? '/job-requests' : '/job-history')}>
            View Job
          </button>
        </div>
      )}

      {hasSystemMessage && (
        <div style={styles.systemNoticeBanner}>🔔 This conversation contains automated TradePower notices.</div>
      )}

      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div style={styles.emptyMessages}>
            <p style={styles.emptyIcon}>💬</p>
            <p style={styles.emptyTitle}>No messages yet</p>
            <p style={styles.emptyText}>Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const prevMsg = idx > 0 ? messages[idx - 1] : null;
            const showDateSeparator = !prevMsg ||
              formatTime(prevMsg.createdAt).split(' ')[0] !== formatTime(msg.createdAt).split(' ')[0];

            if (msg.system) {
              return (
                <div key={msg.id}>
                  {showDateSeparator && (
                    <div style={styles.dateSeparator}><span style={styles.dateSeparatorText}>{formatTime(msg.createdAt)}</span></div>
                  )}
                  <div style={styles.systemMessageWrapper}>
                    <div style={styles.systemMessageCard}>
                      <div style={styles.systemMessageHeader}>
                        <span>🔔</span>
                        <span style={styles.systemMessageTitle}>TradePower System Notice</span>
                        <span style={styles.systemMessageTime}>{formatTime(msg.createdAt)}</span>
                      </div>
                      <div>{msg.text.split('\n').map((line, i) => <p key={i} style={styles.systemMessageLine}>{line || ' '}</p>)}</div>
                      <div style={styles.systemMessageFooter}>🔒 Automated notice — both parties can see this.</div>
                    </div>
                  </div>
                </div>
              );
            }

            const mine = isMyMessage(msg);
            const showSenderName = !prevMsg || prevMsg.senderId !== msg.senderId || prevMsg.system;

            return (
              <div key={msg.id}>
                {showDateSeparator && (
                  <div style={styles.dateSeparator}><span style={styles.dateSeparatorText}>{formatTime(msg.createdAt)}</span></div>
                )}
                <div style={mine ? styles.myMessageRow : styles.theirMessageRow}>
                  {!mine && <div style={styles.avatarCircle}>{(msg.senderName || 'U').charAt(0).toUpperCase()}</div>}
                  <div style={mine ? styles.myMessageGroup : styles.theirMessageGroup}>
                    {showSenderName && !mine && <span style={styles.senderName}>{msg.senderName}</span>}
                    <div style={mine ? styles.myBubble : styles.theirBubble}>
                      <p style={mine ? styles.myMessageText : styles.messageText}>{msg.text}</p>
                    </div>
                    <span style={styles.timeText}>{formatTime(msg.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputBar}>
        <div style={styles.inputWrapper}>
          <textarea
            style={styles.input}
            placeholder="Type a message..."
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            type="button"
            style={newMessage.trim() && !sending ? styles.sendBtn : styles.sendBtnDisabled}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (newMessage.trim() && !sending) handleSend();
            }}
            onClick={(e) => {
              e.preventDefault();
              if (newMessage.trim() && !sending) handleSend();
            }}>
            {sending ? '⏳' : '➤'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadingText: { color: '#666', fontSize: '16px' },
  navbar: { backgroundColor: 'white', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', position: 'sticky', top: 0, zIndex: 100 },
  logo: { fontSize: '22px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', touchAction: 'manipulation' },
  jobBar: { backgroundColor: 'white', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', borderBottom: '2px solid #f0f0f0', flexWrap: 'wrap' },
  jobBarLeft: { flex: 1, minWidth: 0 },
  jobBarTop: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' },
  jobBarName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  jobStatusBadge: { padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  jobBarDesc: { fontSize: '13px', color: '#555', marginBottom: '2px' },
  jobBarDetails: { fontSize: '11px', color: '#999' },
  viewJobBtn: { padding: '8px 14px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0, touchAction: 'manipulation' },
  systemNoticeBanner: { backgroundColor: '#fff8e1', borderBottom: '1px solid #ffc107', padding: '8px 16px', fontSize: '12px', color: '#856404', textAlign: 'center' },
  messagesContainer: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '4px', minHeight: '300px' },
  emptyMessages: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', flex: 1 },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '18px', color: '#1a1a2e', fontWeight: 'bold', marginBottom: '6px' },
  emptyText: { fontSize: '14px', color: '#999' },
  dateSeparator: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0' },
  dateSeparatorText: { fontSize: '11px', color: '#999', backgroundColor: '#f0f2f5', padding: '4px 12px', borderRadius: '10px' },
  systemMessageWrapper: { display: 'flex', justifyContent: 'center', padding: '6px 0' },
  systemMessageCard: { backgroundColor: '#fffbea', border: '1px solid #f6c90e', borderRadius: '12px', padding: '14px', maxWidth: '100%', width: '100%' },
  systemMessageHeader: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(246,201,14,0.4)' },
  systemMessageTitle: { fontSize: '12px', fontWeight: 'bold', color: '#856404', flex: 1 },
  systemMessageTime: { fontSize: '10px', color: '#b8a200' },
  systemMessageLine: { fontSize: '12px', color: '#555', lineHeight: '1.6', margin: 0 },
  systemMessageFooter: { fontSize: '10px', color: '#b8a200', fontStyle: 'italic', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(246,201,14,0.4)' },
  myMessageRow: { display: 'flex', justifyContent: 'flex-end', padding: '2px 0' },
  theirMessageRow: { display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '2px 0' },
  avatarCircle: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1a1a2e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },
  myMessageGroup: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '80%', gap: '2px' },
  theirMessageGroup: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%', gap: '2px' },
  senderName: { fontSize: '11px', color: '#999', paddingLeft: '4px' },
  myBubble: { backgroundColor: '#E94560', borderRadius: '18px 18px 4px 18px', padding: '10px 14px' },
  theirBubble: { backgroundColor: 'white', borderRadius: '18px 18px 18px 4px', padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  messageText: { fontSize: '15px', color: '#333', lineHeight: '1.5', margin: 0 },
  myMessageText: { fontSize: '15px', color: 'white', lineHeight: '1.5', margin: 0 },
  timeText: { fontSize: '11px', color: '#999' },
  inputBar: { backgroundColor: 'white', padding: '12px 16px', paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))', borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, zIndex: 50 },
  inputWrapper: { display: 'flex', gap: '10px', alignItems: 'center' },
  input: { flex: 1, padding: '12px 16px', borderRadius: '24px', border: '1px solid #ddd', fontSize: '16px', resize: 'none', outline: 'none', lineHeight: '1.4', maxHeight: '100px', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white', WebkitAppearance: 'none', color: '#333' },
  sendBtn: { width: '48px', height: '48px', minWidth: '48px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', userSelect: 'none', outline: 'none' },
  sendBtnDisabled: { width: '48px', height: '48px', minWidth: '48px', backgroundColor: '#ddd', color: '#999', border: 'none', borderRadius: '50%', cursor: 'not-allowed', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, touchAction: 'manipulation' },
};

export default Chat;